import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { dashboardService } from "../../services/dashboard.service";

const AR_COLUMNS = ["0-30", "31-60", "61-90", "91-120", "121-180", "181-365", "365+"];

const formatCurrency = (value: string | number | undefined): string => {
  const num = parseFloat(String(value ?? "0"));
  if (isNaN(num)) return "$0";
  return `$${num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Parse ISO date "YYYY-MM-DD"
const parseDotDate = (key: string): number => {
  const d = new Date(key + "T00:00:00");
  return isNaN(d.getTime()) ? 0 : d.getTime();
};

const formatDotDate = (key: string): string => {
  const d = new Date(key + "T00:00:00");
  if (isNaN(d.getTime())) return key;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

export const CurrentARWidget: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["accounts-receivable-period", "DAILY"],
    queryFn: () => dashboardService.getAccountsReceivablePeriod("DAILY"),
  });

  const rows = useMemo(() => {
    if (!data) return [];
    return Object.entries(data)
      .sort(([a], [b]) => parseDotDate(b) - parseDotDate(a))
      .map(([date, metrics]) => {
        const valMap: Record<string, string | number> = {};
        metrics.forEach((m) => {
          valMap[m.metricName] = m.value;
        });
        const total = AR_COLUMNS.reduce((sum, col) => {
          return sum + (parseFloat(String(valMap[col] ?? "0")) || 0);
        }, 0);
        return { date, valMap, total };
      });
  }, [data]);

  return (
    <div className="bg-white border-2 border-border-gray flex flex-col">
      <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
        <h3 className="font-bold text-lg">Current A/R</h3>
        <span className="text-xs sm:text-sm">Accounts Receivable by Aging Period</span>
      </div>

      <div className="flex flex-col">
        {isLoading && <div className="text-center p-4 py-8 text-gray-500">Loading A/R data...</div>}

        {error && (
          <div className="text-center p-4 py-8 text-red-600">
            Failed to load A/R data. Please try again.
          </div>
        )}

        {!isLoading && !error && (
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "380px" }}>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b-2 border-border-gray sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                    Date
                  </th>
                  {AR_COLUMNS.map((col) => (
                    <th
                      key={col}
                      className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                  <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-gray">
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={AR_COLUMNS.length + 2}
                      className="px-3 py-6 text-center text-gray-500"
                    >
                      No data available.
                    </td>
                  </tr>
                ) : (
                  rows.map(({ date, valMap, total }) => (
                    <tr key={date} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium whitespace-nowrap">
                        {formatDotDate(date)}
                      </td>
                      {AR_COLUMNS.map((col) => (
                        <td key={col} className="px-3 py-2 text-right whitespace-nowrap">
                          {formatCurrency(valMap[col])}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">
                        {formatCurrency(total)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
